import { useField, useFormikContext } from "formik";
import React, { KeyboardEvent } from "react";
import CreatableSelect from "react-select/creatable";
import ErrorField from "../errorField";
import FormLabel from "../formLabel";
import { MultiTextInputFieldConfig, FormFieldOption } from "../types";
import { CSSObjectWithLabel, InputActionMeta, OnChangeValue } from "react-select";
import { useFormContextData } from "shared/form/context";
import { getValueForReadOnlyFieldForStaticForm } from "shared/form/util";
import FieldRenderType from "shared/form/renderer/enums/FieldRenderType";
import { isEmpty } from "shared/utils";
import FormOverwriteField from "../overwriteField";
import { ConflictType } from "shared/form/renderer/enums/Conflict";
import FormGroup from "../formGroup";
import { CreatableSelectComponentProps, CustomStyleFunctionState } from "./types";

const createOption = (label: string): FormFieldOption => ({
  label,
  value: label
});

const emptyArray: Array<string> = [];

function canUpdateValues({
  allowDuplicateValue,
  values,
  inputValue
}: {
  allowDuplicateValue: boolean;
  values: Array<string>;
  inputValue: string;
}): boolean {
  if (allowDuplicateValue && inputValue) {
    return true;
  }

  return !!inputValue && !values.includes(inputValue.trim().toLowerCase());
}

/**
 * @description: All inputs are saved in lowercase.
 */
const FormMultiTextInputField = React.memo(function MultiSelectTextInput({
  options = [],
  disabled,
  placeholder,
  allowDuplicateValue = false,
  invalidInputs = emptyArray,
  setUnsavedInput = () => {},
  isMulti = true,
  isValidNewOption = () => false,
  ...props
}: MultiTextInputFieldConfig) {
  const formContextData = useFormContextData();
  const [inputValue, setInputValue] = React.useState<string>("");
  const { setFieldTouched, setFieldValue } = useFormikContext();
  const [{ ...field }, meta] = useField({
    name: props.name,
    validate: props.validate
  });

  async function handleInputChange(newInputValue: string, input: InputActionMeta) {
    if (["input-blur", "menu-close"].includes(input.action) && inputValue && !newInputValue) {
      setUnsavedInput(inputValue);
    } else {
      if (setUnsavedInput) setUnsavedInput(false);
      // don't allow spaces to start with
      (inputValue?.trim()?.length || newInputValue?.trim()?.length) && setInputValue(newInputValue);
    }
    if (!meta.touched) {
      await setFieldTouched(props.name, true);
    }
  }

  function handleChange(newValues: OnChangeValue<FormFieldOption, boolean>) {
    const valuesAsArray = Array.isArray(newValues) ? newValues : [newValues];
    const values = valuesAsArray.map((option: FormFieldOption) => option?.value || "");
    let { qualifiesToRemove = () => true } = props;
    if (qualifiesToRemove(meta.initialValue, values)) {
      setFieldValue(props.name, values);
      return;
    }
  }

  async function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    switch (event.key) {
      case "Enter":
      case "Tab":
        if (setUnsavedInput) await setUnsavedInput(false);

        const canUpdateOnKeyPress = canUpdateValues({
          inputValue,
          values: field.value,
          allowDuplicateValue
        });

        if (canUpdateOnKeyPress) {
          // DANGER: ASK ROHAN / ABHISHEK IF CHANGING BELOW LINE
          setFieldValue(props.name, [...field.value, inputValue.trim().toLowerCase()]);
        } else {
          setFieldValue(props.name, [...field.value]);
        }
        setInputValue("");
        event.preventDefault();
        break;
      default:
        return;
    }
  }

  const handleChangecb = React.useCallback(handleChange, [meta.initialValue, props, setFieldValue]);
  const handleInputChangecb = React.useCallback(handleInputChange, [
    setUnsavedInput,
    inputValue,
    setFieldTouched,
    props.name,
    meta.touched
  ]);
  const handleKeyDowncb = React.useCallback(handleKeyDown, [
    allowDuplicateValue,
    field.value,
    setFieldValue,
    setUnsavedInput,
    props.name,
    inputValue
  ]);

  const fieldName = field.name;
  const value = field.value;

  return (
    <FormGroup className={props.className} fullWidth={props.fullWidth} indented={props.indented}>
      <FormLabel
        htmlFor={field.name}
        label={props.label}
        required={props.required}
        infoLabel={props.infoLabel}
        additionalLabel={props.additionalLabel}
      />
      {formContextData.isReadOnly ? (
        getValueForReadOnlyFieldForStaticForm({ renderType: FieldRenderType.TAGS, value: field.value })
      ) : (
        <div className="multiTextInput">
          <CreatableSelectComponent
            inputId={fieldName}
            options={options}
            isDisabled={disabled}
            inputValue={inputValue}
            isMulti={isMulti}
            onChange={handleChangecb}
            onInputChange={handleInputChangecb}
            onKeyDown={handleKeyDowncb}
            placeholder={placeholder}
            value={value}
            className="customSelect customSelect--multiText"
            classNamePrefix="customSelect"
            invalidInputs={invalidInputs}
            isValidNewOption={isValidNewOption}
            noOptionsMessage={() => {
              return null;
            }}
          />
        </div>
      )}
      {!isEmpty(props.conflictValue) && (
        <FormOverwriteField forFieldName={props.name} disabled={disabled} conflictType={ConflictType.EMAIL_LIST} />
      )}
      <ErrorField meta={meta} />
    </FormGroup>
  );
});

function CreatableSelectComponent({
  inputId,
  options,
  isDisabled,
  inputValue,
  isMulti,
  onChange,
  onInputChange,
  onKeyDown,
  placeholder,
  value,
  invalidInputs,
  className = "customSelect customSelect--multiText",
  classNamePrefix = "customSelect",
  isValidNewOption,
  noOptionsMessage
}: CreatableSelectComponentProps) {
  const customStyles = React.useMemo(() => {
    return {
      multiValue: (base: CSSObjectWithLabel, { data }: CustomStyleFunctionState) => {
        return invalidInputs.includes(data?.value) ? { ...base, backgroundColor: "#ffe6e6" } : base;
      },
      multiValueRemove: (base: CSSObjectWithLabel, { data }: CustomStyleFunctionState) => {
        return { ...base, display: isDisabled ? "none" : "flex" };
      }
    };
  }, [invalidInputs, isDisabled]);

  const { adaptedValues } = React.useMemo(() => {
    let adaptedValues: Array<FormFieldOption> = [];
    if (options.length === 0) {
      adaptedValues =
        (value && value.map((label: string): FormFieldOption => createOption(label && label.toLowerCase()))) || [];
    } else {
      adaptedValues =
        value?.map?.((val: string): FormFieldOption => {
          const option = options.find((option: FormFieldOption) => option.value === val);
          return option || { label: val, value: val };
        }) || [];
    }

    return { adaptedValues };
  }, [options, value]);

  return (
    <CreatableSelect
      inputId={inputId}
      options={options}
      isDisabled={isDisabled}
      styles={customStyles}
      inputValue={inputValue}
      isMulti={isMulti}
      onChange={onChange}
      onInputChange={onInputChange}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      value={adaptedValues}
      className={className}
      classNamePrefix={classNamePrefix}
      isValidNewOption={isValidNewOption}
      noOptionsMessage={noOptionsMessage}
      components={{
        DropdownIndicator: null
      }}
    />
  );
}

export default FormMultiTextInputField;
